// Renders an ApiError in the standard banner shape:
//   [code badge] message  (field: x)
//   ...
//   Request-Id: <id>  [Copy]
//
// Used by FormShell to surface mutation failures; also OK to drop
// inline above a list query result. `error` accepts unknown so
// callers don't have to narrow before rendering.

import { ApiError } from "../../api/errors";
import { Pill } from "./Pill";
import { CopyButton } from "./CopyButton";

interface ErrorBannerProps {
  error: unknown;
  title?: string;
}

export function ErrorBanner({ error, title }: ErrorBannerProps) {
  if (!(error instanceof ApiError)) {
    // Unknown / network errors get a generic shape.
    return (
      <div style={frame("crit")}>
        <div style={header}>
          <Pill tone="crit">error</Pill>
          <strong>{title ?? "Request failed"}</strong>
        </div>
        <div style={body}>{String((error as Error)?.message ?? error)}</div>
      </div>
    );
  }
  return (
    <div style={frame("crit")}>
      <div style={header}>
        <Pill tone="crit">{error.code}</Pill>
        <strong>{title ?? "Request failed"}</strong>
      </div>
      <div style={body}>
        {error.message}
        {error.field && (
          <span style={{ color: "var(--text-dim)", marginLeft: 6 }}>
            (field: <code>{error.field}</code>)
          </span>
        )}
        {error.resource && !error.field && (
          <span style={{ color: "var(--text-dim)", marginLeft: 6 }}>
            (resource: <code>{error.resource}</code>)
          </span>
        )}
      </div>
      {error.requestId && (
        <div style={footer}>
          <span>Request-Id: <code>{error.requestId}</code></span>
          <CopyButton value={error.requestId} label="Copy" />
        </div>
      )}
    </div>
  );
}

const frame = (tone: "crit" | "warn"): React.CSSProperties => ({
  border: `1px solid var(--${tone})`,
  background:
    tone === "crit"
      ? "rgba(248, 113, 113, 0.08)"
      : "rgba(251, 191, 36, 0.08)",
  borderRadius: 6,
  padding: "10px 12px",
  fontSize: "0.85rem",
  color: "var(--text)",
});

const header: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 4,
};

const body: React.CSSProperties = {
  color: "var(--text)",
};

const footer: React.CSSProperties = {
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: "var(--text-dim)",
  fontSize: "0.78rem",
};
